import { LocalizedLabel, SpacedLongDash } from '@mews-commander/ui';
import { Typography } from '@optimus-web/core';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import {
    AlternativePaymentMethod,
    CreditCardPaymentData,
    ExternalPaymentData,
    Payment,
    PaymentType,
    SepaDirectDebitData,
    ServiceOrderType,
    PaymentKind,
} from '@mews-commander/data-access';
import { LocalizedEnumify, LocalizedText, useFeatureFlag } from '@mews/framework';
import { Amount } from '@mews-commander/types';
import { ExternalPaymentType } from '@mews-commander/legacy-data-structures';
import {
    isAmountPositive,
    alternativePaymentMethodRefundTextKeyMap,
    alternativePaymentMethodTextKeyMap,
    paymentTypeTextKeyMap,
    refundTypeTextKeyMap,
    useCommanderSelector,
} from '@mews-commander/utils';
import { getServiceOrder } from '@mews-commander/legacy-entities/src/ducks';
import { useGetInfoRowProps } from '../../AccountsInfoSection/utils';
import { ServiceName } from '../../../../../../components';
import PaymentCardDetail from './PaymentCardDetail';

interface PaymentTypeNameProps {
    payment: Payment;
    hideServiceOrderName?: boolean;
}

export const PaymentTypeName = ({
    payment: { value, data, notes, serviceOrderId, kind },
    hideServiceOrderName,
}: PaymentTypeNameProps) => {
    const reservationLinkToPaymentsEnabled = useFeatureFlag(FeatureFlag.reservationLinkToPaymentsEnabled);

    const isRefund = isAmountPositive(value as Amount | null);
    const labelTextKey = isRefund ? getRefundTypeTextKey(data, kind) : getPaymentTypeTextKey(data);

    const serviceOrder = useCommanderSelector((state) => getServiceOrder(state, { id: serviceOrderId || '' }));
    const reservation =
        serviceOrder?.data.discriminator === ServiceOrderType.Reservation ? serviceOrder.data.value : null;

    const { name } = useGetInfoRowProps({ accountId: reservation?.ownerId || '' });

    return (
        <>
            <LocalizedLabel textKey={labelTextKey} as="div" />
            <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                {renderPaymentDetail(data)}
                {notes ? (
                    <>
                        {SpacedLongDash}
                        {notes}
                    </>
                ) : null}
                {!hideServiceOrderName && reservation && reservationLinkToPaymentsEnabled && (
                    <>
                        {name ? (
                            <>
                                {SpacedLongDash}
                                {name}
                            </>
                        ) : null}
                        {SpacedLongDash}
                        <ServiceName id={reservation.serviceId} /> {reservation.number}
                    </>
                )}
            </Typography>
        </>
    );
};

const getPaymentTypeTextKey = (data: Payment['data']): Mews.TextKey => {
    if (data.discriminator === PaymentType.Ghost && data.value.originalPaymentData) {
        // @ts-expect-error originalPaymentData type is incorrect, annotation should be fixed
        return getPaymentTypeTextKey(data.value.originalPaymentData);
    }

    if (data.discriminator === PaymentType.Alternative) {
        return alternativePaymentMethodTextKeyMap[data.value.data.discriminator];
    }

    return paymentTypeTextKeyMap[data.discriminator];
};

const getRefundTypeTextKey = (data: Payment['data'], kind: Payment['kind']): Mews.TextKey => {
    if (kind === PaymentKind.Chargeback) {
        return TextKey.Chargeback;
    }

    if (data.discriminator === PaymentType.Ghost && data.value.originalPaymentData) {
        // @ts-expect-error originalPaymentData type is incorrect, annotation should be fixed
        return getRefundTypeTextKey(data.value.originalPaymentData, kind);
    }

    if (data.discriminator === PaymentType.Alternative) {
        return alternativePaymentMethodRefundTextKeyMap[data.value.data.discriminator];
    }

    if (data.discriminator === PaymentType.Invoice) {
        return TextKey.ReceivableInvoicePayment;
    }

    return refundTypeTextKeyMap[data.discriminator];
};

const renderPaymentDetail = (data: Payment['data']): React.ReactNode => {
    if (data.discriminator === PaymentType.Ghost && data.value.originalPaymentData) {
        // @ts-expect-error originalPaymentData type is incorrect, annotation should be fixed
        return renderPaymentDetail(data.value.originalPaymentData);
    }

    if (data.discriminator === PaymentType.External) {
        return <ExternalPaymentDetail {...data.value} />;
    }

    if (data.discriminator === PaymentType.CreditCard) {
        return <CreditCardPaymentDetail {...data.value} />;
    }

    if (
        data.discriminator === PaymentType.Alternative &&
        data.value.data.discriminator === AlternativePaymentMethod.SepaDirectDebit
    ) {
        return <SepaDirectDebitPaymentDetail {...data.value.data.value} />;
    }

    return null;
};

const ExternalPaymentDetail = ({ type, identifier }: ExternalPaymentData) => {
    const enumValue = ExternalPaymentType.getByName(type);

    return (
        <>
            {SpacedLongDash}
            <LocalizedEnumify enumValue={enumValue} />
            {identifier ? (
                <>
                    {SpacedLongDash} {identifier}
                </>
            ) : null}
        </>
    );
};

const CreditCardPaymentDetail = ({ preauthorizationId, paymentCardId }: CreditCardPaymentData) => (
    <>
        {paymentCardId ? (
            <>
                {SpacedLongDash}
                <PaymentCardDetail paymentCardId={paymentCardId} />
            </>
        ) : null}
        {preauthorizationId ? (
            <>
                {SpacedLongDash}
                <LocalizedText textKey={TextKey.Preauthorization} />
            </>
        ) : null}
    </>
);

const SepaDirectDebitPaymentDetail = ({ lastFourIbanDigitsWithSepa }: SepaDirectDebitData) => (
    <>
        {SpacedLongDash}
        {lastFourIbanDigitsWithSepa}
    </>
);
